import React, { useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "contexts/AuthContext";
import { DialogProvider } from "contexts/DialogContext";
import { I18nProvider } from "i18n";
import AppLayout from "components/layout/AppLayout";
import PublicLayout from "components/layout/PublicLayout";
import Login from "pages/Login";
import OidcCallback from "pages/OidcCallback";
import Dashboard from "pages/Dashboard";
import PublicHome from "pages/public/Home";
import PublicEventGallery from "pages/public/EventGallery";
import PublicPhotoView from "pages/public/PhotoView";
import Events from "pages/events/Events";
import EventForm from "pages/events/EventForm";
import EventDetail from "pages/events/EventDetail";
import Championships from "pages/championships/Championships";
import ChampionshipForm from "pages/championships/ChampionshipForm";
import ChampionshipDetail from "pages/championships/ChampionshipDetail";
import Sports from "pages/sports/Sports";
import SportForm from "pages/sports/SportForm";
import SportCategories from "pages/sports/SportCategories";
import SportCategoryForm from "pages/sports/SportCategoryForm";
import PublicChampionship from "pages/public/Championship";
import Accounts from "pages/admin/Accounts";
import AccountDetail from "pages/admin/AccountDetail";
import AccountForm from "pages/admin/AccountForm";
import AdminSettings from "pages/admin/Settings";
import "./App.css";

/**
 * Duas areas, um unico SPA:
 *
 * - **publica** (`/`) — a area PADRAO. Anonima: lista os eventos publicados e
 *   navega as fotos. Tema claro e layout proprio (`PublicLayout`).
 * - **administrativa** (`/app`) — tudo que exige login, inclusive a tela de
 *   login (`/app/login`) e o retorno do SSO (`/app/sso/callback`).
 *
 * O modo escuro pertence so a area administrativa; a publica e uma vitrine e
 * fica sempre no tema claro (o `PublicLayout` remove a classe `dark`).
 */
function App() {
  const [darkMode, setDarkMode] = useState(true);

  return (
    <div className="App">
      <BrowserRouter>
        {/* EN e o fallback; apos o login adotamos o idioma efetivo do usuario. */}
        <I18nProvider>
          <AuthProvider>
            <DialogProvider>
              <Routes>
                {/* ---------------- Area publica (padrao) ---------------- */}
                <Route element={<PublicLayout />}>
                  <Route path="/" element={<PublicHome />} />
                  {/* Campeonato: a competicao que agrupa as etapas. Aceita o
                      slug (URL bonita) ou o UUID. */}
                  <Route path="/championships/:key" element={<PublicChampionship />} />
                  <Route path="/events/:id" element={<PublicEventGallery />} />
                  {/* URL canonica de uma foto (a que e compartilhada). Para um
                      crawler de rede social o nginx desvia esta mesma URL ao
                      backend, que responde as meta tags og:image. */}
                  <Route path="/photo/:id" element={<PublicPhotoView />} />
                </Route>

                {/* ------------- Area administrativa (/app) -------------- */}
                <Route path="/app/login" element={<Login />} />
                <Route path="/app/sso/callback" element={<OidcCallback />} />

                <Route
                  element={<AppLayout darkMode={darkMode} setDarkMode={setDarkMode} />}
                >
                  <Route path="/app/dashboard" element={<Dashboard />} />

                  <Route path="/app/championships" element={<Championships />} />
                  <Route path="/app/championships/new" element={<ChampionshipForm />} />
                  <Route path="/app/championships/:id" element={<ChampionshipDetail />} />
                  <Route
                    path="/app/championships/:id/edit"
                    element={<ChampionshipForm />}
                  />

                  <Route path="/app/sports" element={<Sports />} />
                  <Route path="/app/sports/new" element={<SportForm />} />
                  <Route path="/app/sports/:id/edit" element={<SportForm />} />
                  <Route path="/app/sport-categories" element={<SportCategories />} />
                  <Route
                    path="/app/sport-categories/new"
                    element={<SportCategoryForm />}
                  />
                  <Route
                    path="/app/sport-categories/:id/edit"
                    element={<SportCategoryForm />}
                  />

                  <Route path="/app/events" element={<Events />} />
                  <Route path="/app/events/new" element={<EventForm />} />
                  {/* Detalhe de objeto: sempre janela/rota propria, nunca modal */}
                  <Route path="/app/events/:id" element={<EventDetail />} />
                  <Route path="/app/events/:id/edit" element={<EventForm />} />

                  <Route path="/app/admin/accounts" element={<Accounts />} />
                  <Route path="/app/admin/accounts/new" element={<AccountForm />} />
                  <Route path="/app/admin/accounts/:id" element={<AccountDetail />} />
                  <Route path="/app/admin/accounts/:id/edit" element={<AccountForm />} />

                  <Route path="/app/admin/settings" element={<AdminSettings />} />
                </Route>

                <Route path="/app" element={<Navigate to="/app/dashboard" replace />} />
                <Route path="/app/*" element={<Navigate to="/app/dashboard" replace />} />

                {/* Qualquer outra rota pertence a area publica. */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </DialogProvider>
          </AuthProvider>
        </I18nProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
